import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { APP_STORE_URL, PLAY_STORE_URL, isIOSDevice } from "../config/appStores";
import { trackAppPromptView, trackAppPromptTap } from "../utils/analytics";

/**
 * AppBridgeCard: the ask for the app, made at the one moment the shopper has a
 * reason to want it, right after an order, when they want to follow it.
 *
 * Says the order follows them (phone sign-in finds the same account, so the
 * order is already in the app) and leaves the page as a real alternative, so
 * nobody feels pushed. On a phone it offers that phone's store only; on a
 * computer, both.
 */
const AppBridgeCard = ({ surface = "order_done" }) => {
  const isPhone = useMemo(
    () => typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ""),
    []
  );
  const onIOS = useMemo(() => isIOSDevice(), []);

  useEffect(() => {
    trackAppPromptView(surface);
  }, [surface]);

  const stores = isPhone
    ? [onIOS ? { key: "app_store", label: "Get it on the App Store", url: APP_STORE_URL } : { key: "play", label: "Get it on Google Play", url: PLAY_STORE_URL }]
    : [
        { key: "play", label: "Google Play", url: PLAY_STORE_URL },
        { key: "app_store", label: "App Store", url: APP_STORE_URL },
      ];

  return (
    <Card aria-labelledby="app-bridge-title">
      <Title id="app-bridge-title">Follow this order live in the Lusuku app</Title>
      <Body>
        Sign in to the app with the same number and this order is already there,
        from the vendor to your door.
      </Body>
      <Stores>
        {stores.map((store) => (
          <StoreLink
            key={store.key}
            href={store.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAppPromptTap(surface, store.key)}
          >
            {store.label}
          </StoreLink>
        ))}
      </Stores>
      <Aside>No app? You can keep following it on this page.</Aside>
    </Card>
  );
};

const Card = styled.section`
  margin-top: 16px;
  padding: 20px 22px;
  border-radius: 14px;
  background: #324d54;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.25;
  font-weight: 800;
`;

const Body = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #e4ece6;
`;

const Stores = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const StoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  background: #ffffff;
  color: #15232a;
  font-weight: 800;
  font-size: 15px;
  text-decoration: none;

  &:hover {
    background: #eef3ea;
    color: #15232a;
  }
`;

const Aside = styled.span`
  font-size: 13px;
  color: #c9d6d0;
`;

export default AppBridgeCard;
